import Immutable from 'immutable';
import {
	AlarmCenterIcon,
	AlarmIcon,
	AlarmPlusIcon,
	AssistanceRequestIcon, BuildingIcon, DebugIcon,
	EmailIcon,
	GeofenceIcon, IOIcon,
	LocateIcon, MsTeamsIcon, NotificationIcon,
	SendIcon,
	SMSIcon, TeamsIcon,
	TelegramIcon, TimerIcon,
	VoiceMessageIcon,
	WebhookIcon,
} from '../icons';
import _min from 'lodash/min';
import _max from 'lodash/max';
import SafeguardIcon from '../../components/Icon/SafeguardIcon';
import {isValidPhoneNumber} from "../functions";
import {forAnyObject} from "./Scenario";
import {moduleIds} from "./Module";

const validator = require('validator');

export const scenarioActionTypes = {
	showAsAlert: 'showAsAlert',
	osm: 'osm',
	email: 'email',
	voiceMessage: 'voiceMessage',
	sms: 'sms',
	startTracking: 'startTracking',
	safeguardAlarm: 'safeguardAlarm',
	geofencePresence: 'geofencePresence',
	changeTeam: 'changeTeam',
	createAlarmOnBehalfOf: 'createAlarmOnBehalfOf',
	createAlarm: 'createAlarm',
	webhook: 'webhook',
	assistanceRequest: 'assistanceRequest',
	sendTelegramMessage: 'sendTelegramMessage',
	requestAppLogs: 'requestAppLogs',
	sendToARC: 'sendToARC',
	multiTenant: 'multiTenant',
	setDigitalOutput: 'setDigitalOutput',
	resendLastOSM: 'resendLastOSM',
	toggleTimer: 'toggleTimer',
	lowPrioSIA: 'lowPrioSIA',
	appNotification: 'appNotification',
	msTeams: 'msTeams',
	clbEvent: 'clbEvent'
};
export const notForSimpleViewActions = [
	scenarioActionTypes.multiTenant,
	scenarioActionTypes.assistanceRequest,
	scenarioActionTypes.webhook,
	scenarioActionTypes.geofencePresence,
	scenarioActionTypes.changeTeam,
	scenarioActionTypes.msTeams,
	scenarioActionTypes.clbEvent
];
// With ISO 27001 / NEN 7510 enabled, only an account manager (outside presentation mode) may add or change webhook actions
export function canManageWebhookActions(user, presentationMode) {
	const hasComplianceModule = user.hasModule(moduleIds.Iso27001) || user.hasModule(moduleIds.Nen7510);
	return !hasComplianceModule || (user.isShadowAdmin && !presentationMode);
}
export const actionTypeIcons = {
	[scenarioActionTypes.createAlarmOnBehalfOf]: AlarmPlusIcon,
	[scenarioActionTypes.showAsAlert]: AlarmPlusIcon,
	[scenarioActionTypes.osm]: SendIcon,
	[scenarioActionTypes.email]: EmailIcon,
	[scenarioActionTypes.voiceMessage]: VoiceMessageIcon,
	[scenarioActionTypes.sms]: SMSIcon,
	[scenarioActionTypes.safeguardAlarm]: SafeguardIcon,
	[scenarioActionTypes.startTracking]: LocateIcon,
	[scenarioActionTypes.geofencePresence]: GeofenceIcon,
	[scenarioActionTypes.changeTeam]: TeamsIcon,
	[scenarioActionTypes.createAlarm]: AlarmIcon,
	[scenarioActionTypes.webhook]: WebhookIcon,
	[scenarioActionTypes.assistanceRequest]: AssistanceRequestIcon,
	[scenarioActionTypes.sendTelegramMessage]: TelegramIcon,
	[scenarioActionTypes.sendToARC]: AlarmCenterIcon,
	[scenarioActionTypes.requestAppLogs]: DebugIcon,
	[scenarioActionTypes.multiTenant]: BuildingIcon,
	[scenarioActionTypes.setDigitalOutput]: IOIcon,
	[scenarioActionTypes.resendLastOSM]: SendIcon,
	[scenarioActionTypes.toggleTimer]: TimerIcon,
	[scenarioActionTypes.lowPrioSIA]: AlarmCenterIcon,
	[scenarioActionTypes.appNotification]: NotificationIcon,
	[scenarioActionTypes.msTeams]: MsTeamsIcon,
	[scenarioActionTypes.clbEvent]: WebhookIcon,
};
export const actionTypeLabels = {
	[scenarioActionTypes.createAlarmOnBehalfOf]: 'createAlarmOnBehalfOfOtherDevice',
	[scenarioActionTypes.showAsAlert]: 'showInAlerts',
	[scenarioActionTypes.sms]: 'smsMessage',
	[scenarioActionTypes.email]: 'emailMessage',
	[scenarioActionTypes.voiceMessage]: 'voiceMessage',
	[scenarioActionTypes.osm]: 'scenario.osm',
	[scenarioActionTypes.safeguardAlarm]: 'createSafeguardAlarm',
	[scenarioActionTypes.startTracking]: 'startTracking',
	[scenarioActionTypes.geofencePresence]: 'geofencePresence.action',
	[scenarioActionTypes.changeTeam]: 'changeTeam',
	[scenarioActionTypes.createAlarm]: 'createAnAlarm',
	[scenarioActionTypes.webhook]: 'callWebhook',
	[scenarioActionTypes.assistanceRequest]: 'sendAssistanceRequest',
	[scenarioActionTypes.sendTelegramMessage]: 'sendTelegramMessage',
	[scenarioActionTypes.sendToARC]: 'sendAlarmToARC',
	[scenarioActionTypes.requestAppLogs]: 'requestLogs',
	[scenarioActionTypes.multiTenant]: 'multiTenant',
	[scenarioActionTypes.setDigitalOutput]: 'setDigitalOutput',
	[scenarioActionTypes.resendLastOSM]: 'resendLastOSM',
	[scenarioActionTypes.toggleTimer]: 'toggleTimer',
	[scenarioActionTypes.lowPrioSIA]: 'sendLowPrioSIA',
	[scenarioActionTypes.appNotification]: 'appNotification',
	[scenarioActionTypes.msTeams]: 'msTeams',
	[scenarioActionTypes.clbEvent]: 'eventToCLB',
};

const ScenarioActionModel = Immutable.Record({
	id: 0,
	type: '',
	name: '',
	config: {}
});

export default class ScenarioAction extends ScenarioActionModel {
	id: number;
	type: string;
	name: string;
	config: any;

	static gpsPlaceholders = ['location', 'geofence_sm', 'geofences', 'beacons', 'beacon', 'gmapsUrl', 'site'];
	
	static containsPositionPlaceholders = (text) => {
		if(!text) return false;
		return !!ScenarioAction.gpsPlaceholders.filter(p => text.indexOf(`{{${p}}}`) >= 0).length;
	};

	get Icon() {
		return actionTypeIcons[this.get('type')];
	}

	get canSave() {
		const conf = this.get('config');
		if(this.get('type') === scenarioActionTypes.sendToARC) return true;
		if(this.get('type') === scenarioActionTypes.changeTeam) {
			if(conf.action !== 'join' && conf.action !== 'leave') return false;
			return Array.isArray(conf.teamIds) && !!conf.teamIds.length;
		}
		if(this.get('type') === scenarioActionTypes.startTracking) {
			return !!conf.interval && !!conf.duration;
		}
		if(this.get('type') === scenarioActionTypes.createAlarmOnBehalfOf) {
			return conf.deviceId > 0;
		}
		if(this.get('type') === scenarioActionTypes.email) {
			return conf.recipients && conf.recipients.length >= 1 && conf.text;
		}
		if(this.get('type') === scenarioActionTypes.voiceMessage) {
			if(conf.originator && conf.originator !== 'self' && !isValidPhoneNumber(conf.originator)) return false;
			return conf.recipients && conf.recipients.length >= 1 && conf.text && conf.language;
		}
		if(this.get('type') === scenarioActionTypes.sms) {
			return conf.recipients && conf.recipients.length >= 1 && conf.text;
		}
		if(this.get('type') === scenarioActionTypes.msTeams) {
			return validator.isURL(conf.url || '') && conf.text?.length >= 1;
		}
		if(this.get('type') === scenarioActionTypes.osm || this.get('type') === scenarioActionTypes.appNotification) {
			return conf.recipients && conf.text && (conf.recipients.devices.length || conf.recipients.teams.length || conf.recipients.geofences.length);
		}
		if(this.get('type') === scenarioActionTypes.assistanceRequest) {
			if(conf.cancelWhenEnoughAccepted && !(+conf.accepts >= 1)) return false;
			return conf.name && conf.recipients && conf.text && (conf.recipients.devices.length || conf.recipients.teams.length || conf.recipients.geofences.length);
		}
		if([scenarioActionTypes.safeguardAlarm, scenarioActionTypes.lowPrioSIA].indexOf(this.get('type')) >= 0) {
			return !!conf.text;
		}
		if(this.get('type') === scenarioActionTypes.sendTelegramMessage) {
			return !!conf.chatId && !!conf.text;
		}
		if(this.get('type') === scenarioActionTypes.clbEvent) {
			return !!conf.url && validator.isURL(conf.url);
		}
		if(this.get('type') === scenarioActionTypes.geofencePresence) {
			return !!conf.status;
		}
		if(this.get('type') === scenarioActionTypes.setDigitalOutput) {
			return +conf.outputs?.length > 0 && conf.recipients && (conf.recipients.devices.length || conf.recipients.teams.length || conf.recipients.geofences.length);
		}
		if(this.get('type') === scenarioActionTypes.webhook) {
			return !!conf.url && !!conf.method && validator.isURL(conf.url) && conf.url.indexOf('sosvolaris.com') === -1;
		}
		return [
			scenarioActionTypes.createAlarm,
			scenarioActionTypes.showAsAlert
		].indexOf(this.get('type') >= 0);
	}

	get possiblyTriggersOtherScenarios() {
		return [
			scenarioActionTypes.createAlarm
		].indexOf(this.get('type')) >= 0;
	}
	
	get needsScenario() {
		return !!{
			[scenarioActionTypes.assistanceRequest]: 1
		}[this.get('type')];
	}

	get canTest() {
		return !{
			[scenarioActionTypes.multiTenant]: 1,
			[scenarioActionTypes.startTracking]: 1,
			[scenarioActionTypes.geofencePresence]: 1,
			[scenarioActionTypes.changeTeam]: 1,
			[scenarioActionTypes.toggleTimer]: 1,
			[scenarioActionTypes.sendToARC]: 1,
			[scenarioActionTypes.clbEvent]: 1
		}[this.get('type')];
	}

	creditCosts(devices, teams, geofences, beacons, alarmTypeNames) {
		if([scenarioActionTypes.sms, scenarioActionTypes.voiceMessage].indexOf(this.get('type')) === -1) {
			return { min: 0, max: 0 };
		}
		const locations = geofences.toJS().concat(beacons.toJS());
		if(!devices?.toJS()) devices = [];
		let min = 0;
		let max = 0;
		
		const scenarioRecipients = this.get('config').recipients || [];
		const geofenceIds = scenarioRecipients.filter(r => r.indexOf('{{g-') === 0).map(r => +r.replace('{{g-', '').replace('}}', ''));
		if(geofenceIds.length) {
			return { min: 10000, max: 10000 };
		}
		const teamDeviceCount =  new Set(scenarioRecipients
			.filter(r => r.indexOf('{{t-') === 0)
				.map(r => +r.replace('{{t-', '').replace('}}', ''))
				.map(tId => teams.find(t => t.id === tId).deviceIds)
				.flat()).size;
		
		let minText = this.get('config').text || '';
		let maxText = this.get('config').text || '';
		if(minText.indexOf('{{device}}') >= 0) {
			minText = minText.split('{{device}}').join(Array(_min(devices.toJS().map(l => l.name.length))).join('-'));
			maxText = maxText.split('{{device}}').join(Array(_max(devices.toJS().map(l => l.name.length))).join('-'));
		}
		if(minText.indexOf('{{beacons}}') >= 0) {
			minText = minText.split('{{beacons}}').join(Array(_min(beacons.toJS().map(l => l.name.length))).join('-'));
			maxText = maxText.split('{{beacons}}').join(Array(_max(beacons.toJS().map(l => l.name.length))).join('-'));
		}
		if(minText.indexOf('{{geofences}}') >= 0) {
			minText = minText.split('{{geofences}}').join(Array(_min(geofences.toJS().map(l => l.name.length))).join('-'));
			maxText = maxText.split('{{geofences}}').join(Array(_max(geofences.toJS().map(l => l.name.length))).join('-'));
		}
		if(minText.indexOf('{{geofence_sm}}') >= 0) {
			minText = minText.split('{{geofence_sm}}').join(Array(_min(geofences.toJS().map(l => l.name.length))).join('-'));
			maxText = maxText.split('{{geofence_sm}}').join(Array(_max(geofences.toJS().map(l => l.name.length))).join('-'));
		}
		if(minText.indexOf('{{location}}') >= 0) {
			minText = minText.split('{{location}}').join(Array(_min(locations.map(l => l.name.length))).join('-'));
			maxText = maxText.split('{{location}}').join(Array(_max(locations.map(l => l.name.length))).join('-'));
		}
		if(minText.indexOf('{{date}}') >= 0) {
			minText = minText.split('{{date}}').join('01-01-2000');
			maxText = maxText.split('{{date}}').join('01-01-2000');
		}
		if(minText.indexOf('{{time}}') >= 0) {
			minText = minText.split('{{time}}').join('10:10');
			maxText = maxText.split('{{time}}').join('10:10');
		}
		if(minText.indexOf('{{alarmType}}') >= 0) {
			minText = minText.split('{{alarmType}}').join(Array(_min(alarmTypeNames.map(a => a.length))).join('-'));
			maxText = maxText.split('{{alarmType}}').join(Array(_max(alarmTypeNames.map(a => a.length))).join('-'));
		}
		if(minText.indexOf('{{gmapsUrl}}') >= 0) {
			minText = minText.split('{{gmapsUrl}}').join(Array(23).join('-'));
			maxText = maxText.split('{{gmapsUrl}}').join(Array(26).join('-'));
		}
		if(minText.indexOf('{{site}}') >= 0) {
			minText = minText.split('{{site}}').join("1234567890");
			maxText = maxText.split('{{site}}').join("1234567890");
		}
		min = Math.ceil(minText.length / this.messageLength);
		max = Math.ceil(maxText.length / this.messageLength);

		const recipientCount = scenarioRecipients.filter(r => r.indexOf('{{') === -1).length + teamDeviceCount;
		min *= recipientCount;
		max *= recipientCount;
			
		return { min, max };
	}


	get typeName(): string {
		return actionTypeLabels[this.get('type')] || '';
	}

	get messageLength() {
		if(this.get('type') === scenarioActionTypes.safeguardAlarm) return 160;
		if(this.get('type') === scenarioActionTypes.sms) return 160;
		if(this.get('type') === scenarioActionTypes.voiceMessage) return 510;
		return null;
	}

	containsDevices(devices, user, scenario) {
		const json = JSON.stringify(this.get('config'));
		if(this.get('config')?.recipients?.devices?.indexOf(forAnyObject) >= 0) {
			return scenario.canPerformAction(
				this.get('type'),
				devices,
				user,
				true
			) && devices.find(d => {
				return this.canPerformAction(d);
			});
		}
		const deviceIds = devices.map(d => d.id);
		return deviceIds.indexOf(this.get('config')?.deviceId) >= 0 ||
			!!deviceIds.find(dId => json.indexOf(`{{d-${dId}}}`) >= 0) ||
			this.get('config')?.deviceIds?.find(dId => deviceIds.indexOf(dId) >= 0) ||
			this.get('config')?.recipients?.devices?.find(dId => deviceIds.indexOf(dId) >= 0);
	}
	containsTeamIds(teamIds) {
		return teamIds.indexOf(this.get('config')?.teamId) >= 0 ||
			this.get('config')?.teamIds?.find(tId => teamIds.indexOf(tId) >= 0) ||
			this.get('config')?.recipients?.teams?.find(tId => teamIds.indexOf(tId) >= 0);
	}
	containsGeofenceIds(geofenceIds) {
		return geofenceIds.indexOf(this.get('config')?.geofenceId) >= 0 ||
			this.get('config')?.geofenceIds?.find(gId => geofenceIds.indexOf(gId) >= 0) ||
			this.get('config')?.recipients?.geofences?.find(gId => geofenceIds.indexOf(gId) >= 0);
	}
	
	canPerformAction(device) {
		switch(this.get('type')) {
			case scenarioActionTypes.osm:
			case scenarioActionTypes.resendLastOSM:
				return device.can('osm');
			case scenarioActionTypes.assistanceRequest:
			case scenarioActionTypes.appNotification: 
				return device.isApp;
			case scenarioActionTypes.toggleTimer:
				return device.can('timer');
			case scenarioActionTypes.setDigitalOutput:
				return device.can('setDigitalOutput');
			case scenarioActionTypes.createAlarmOnBehalfOf:
				return device.can('allAlarmTypes');
			default: return true;
		}
	}

	clone() {
		return this.set('type', this.get('type'));
	}

	constructor(props) {
		super(props);

		const config = this.get('config');
		if(!config.whenEmpty) {
			config.whenEmpty = {};
		}
		ScenarioAction.gpsPlaceholders.forEach(p => {
			if (!config.whenEmpty[p]) {
				config.whenEmpty[p] = '[?]';
			}
		});
		this.set('config', config);
	}

	static create(type, initConfig = {}, language = 'nl-NL') {
		return new ScenarioAction({
			id: 0,
			config: initConfig,
			type,
			language
		});
	}
}
